import React, { useState } from 'react'
import injectSheet from 'react-jss'
import FormInput from 'pobble-components/dist/form-elements/form-input'
import FormCheckbox from 'pobble-components/dist/form-elements/form-checkbox'
import { Link, withRouter } from 'react-router-dom'
import Button from 'pobble-components/dist/button'
import { useRecaptcha } from 'pobble-components/dist/recaptcha'
import { apiClient } from 'pobble-components/dist/api'
import {
  redirectAfterAuth,
  setToken
} from 'pobble-components/dist/auth-helpers'
import EVENTS from '../../../events'
import { getFinishSignupStatus } from '../../../common/api/me.selectors'
import { apiGetMe } from '../../../common/api/profile'
import ButtonElement from 'pobble-components/dist/button-element'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye'
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { REDIRECT_AFTER_AUTH_FLAG } from '../../../common/redirect-after-auth-flag'

function onSignIn(jwt, history, useTestRedirect) {
  setToken(jwt)
  EVENTS.onFormSignin().then(() => {
    apiGetMe({
      include:
        'account.finish_signup_status,account.teacher_type,account.pending_teacher_type'
    }).then(res => {
      const FSUStatus = getFinishSignupStatus(res.data)
      !FSUStatus.finished
        ? history.push('/finish-signup')
        : redirectAfterAuth(useTestRedirect)
    })
  })
}

export const SignInForm = withRouter(
  injectSheet({
    fields: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      '& input': {
        textAlign: 'left !important',
        '&::placeholder': {
          color: 'var(--gray---)'
        }
      }
    },
    options: {
      margin: '16px 0',
      display: 'flex',
      justifyContent: 'space-between',
      font: 'var(--body--)',
      alignItems: 'center'
    },
    remember: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      color: 'var(--gray--)',
      '&:hover': {
        color: 'var(--gray)'
      }
    },
    forgotLink: {
      color: 'var(--gray--)',
      fontWeight: 'normal',
      '&:hover, &:focus': {
        color: 'var(--gray)'
      }
    },
    submit: {
      margin: '8px 0 0'
    },
    rememberCheckbox: {
      '& > span > span': {
        borderColor: '#ddd',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        '& svg': {
          color: 'var(--blue)'
        }
      }
    },
    error: {
      color: 'var(--red)',
      fontWeight: 'bold',
      padding: '14px 16px',
      borderRadius: '4px',
      margin: '24px 0 16px',
      background: '#fdece8',
      textAlign: 'center'
    },
    password: {
      position: 'relative',
      '& input': {
        paddingRight: '40px'
      }
    },
    togglePassword: {
      position: 'absolute',
      right: '0',
      top: '0',
      height: '100%',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ccc',
      '&:hover': {
        color: 'var(--gray--)'
      }
    }
  })(({ classes, history }) => {
    const {
      Recaptcha,
      recaptchaPending,
      recaptchaProps,
      getReCaptchaToken
    } = useRecaptcha()

    const [error, setError] = useState(null)
    const [showPassword, setShowPassword] = useState(false)
    const [pending, setPending] = useState(false)
    const redirectVariant = useFeatureFlagVariantKey(REDIRECT_AFTER_AUTH_FLAG)

    const errorRef = error?.response?.data?.errors?.[0]?.err

    const applyRecaptcha = errorRef === 'e0042'

    return (
      <>
        {applyRecaptcha && <Recaptcha {...recaptchaProps} />}
        <form
          className={classes.form}
          onSubmit={e => {
            e.preventDefault()
            setPending(true)
            const options = {
              email: e.target.email.value,
              password: e.target.password.value,
              remember_me: e.target.remember.checked
            }
            if (applyRecaptcha) {
              getReCaptchaToken(token =>
                signIn({ ...options, recaptcha_token: token })
              )
            } else {
              signIn(options)
            }

            function signIn(options) {
              apiClient
                .post(`/v3/email_authentication`, options)
                .then(res => {
                  onSignIn(res.data.jwt, history, redirectVariant === 'test')
                })
                .catch(e => {
                  setPending(false)
                  setError(e)
                })
            }
          }}>
          <div className={classes.fields}>
            <FormInput name="email" placeholder="Email" type="email" required />
            <div className={classes.password}>
              <FormInput
                name="password"
                placeholder="Password"
                type={!showPassword ? 'password' : 'text'}
                required
              />
              <ButtonElement
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Show"
                className={classes.togglePassword}>
                {!showPassword && <FontAwesomeIcon icon={faEye} />}
                {showPassword && <FontAwesomeIcon icon={faEyeSlash} />}
              </ButtonElement>
            </div>
          </div>
          <div className={classes.options}>
            <label className={classes.remember}>
              <FormCheckbox
                name="remember"
                className={classes.rememberCheckbox}
                defaultChecked
              />
              Remember for 30 days
            </label>
            <Link className={classes.forgotLink} to="/forgot-password">
              Forgot password?
            </Link>
          </div>
          {!!error && (
            <p className={classes.error}>
              {applyRecaptcha
                ? 'Please sign in again to submit ReCaptcha'
                : errorRef === 'e0004'
                  ? 'Email or password is incorrect'
                  : 'Something went wrong, try again later'}
            </p>
          )}
          <Button
            disabled={pending && recaptchaPending}
            type="submit"
            darkBlue
            className={classes.submit}
            wide
            tall>
            {!pending && 'Sign in'}
            {pending && 'Signing in...'}
          </Button>
        </form>
      </>
    )
  })
)
